import React, { useState, useEffect, useCallback } from 'react';
import dayjs from 'dayjs';
import Link from 'next/link';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import {
    Box,
    Type,
    P,
    Grid,
} from '@saladbob/sassafras'

import { fetchAdmin } from '../../hooks/useSwr';

import { socket } from '../service/socket';

import style from './bracket.module.css';

import { Game as GameType, Team } from '../../types';

type TeamProps = {
    team: Team,
    loser?: Team,
    place: string,
    textAlign?: string,
};

type StatusProps = {
    status: string,
    color?: string,
};

type GameProps = {
    game: GameType,
    topLabel?: boolean,
    bottomLabel?: boolean,
    tb?: boolean,
};

dayjs.extend(utc);
dayjs.extend(timezone);

dayjs.tz.setDefault('America/New_York');

function truncateString(str, num) {
    if (!str || str.length <= num) {
        return str
    }
    return str.slice(0, num) + '...'
}

const TeamCard: React.FC<TeamProps> = ({ team, loser, place, textAlign }) => {
    return (
    <P
        className={`${style.gameTeamName}`}
        font="control"
        weight="bold"
        ps={textAlign ? '0' : 'md'}
        pe={textAlign ? 'md' : '0'}
        width="auto"
        decoration={loser && loser.id === team.id ? 'line-through' : null}
        color={loser && loser.id === team.id ? 'neutral' : null}
        align={textAlign}
    >
        {team ? truncateString(team.name, 20) : place}
    </P>
)};

const Status: React.FC<StatusProps> = ({status, color = "black"}) => {
    return (
    <Box width="100%" height="100%" display="inline-flex" txtColor="white" bgColor={color} align="center" justify="center">
        <Type font="small" align="center" weight="bold" margin="0">{status}</Type>
    </Box>
)};

const Game: React.FC<GameProps> = ({ game }) => {
    const { admin } = fetchAdmin();
    const [score1, setScore1] = useState(game?.team1Score || 0);
    const [score2, setScore2] = useState(game?.team2Score || 0);
    const [status, setStatus] = useState(game?.status || 'pending');
    const [team1, setTeam1] = useState(game?.team1);
    const [team2, setTeam2] = useState(game?.team2);
    const [loser, setLoser] = useState(game?.loser);

    const updateScores = useCallback((data) => {
        if (data.id === game.id) {
            setScore1(data.team1Score);
            setScore2(data.team2Score);
        }
    }, [game.id]);

    const updateStatus = useCallback((data) => {
        if (data.id === game.id) {
            setStatus(data.status);
        }
    }, [game.id]);

    const updateTeam1 = useCallback((data) => {
        if (data.id === game.id) {
            setTeam1(data.team1);
        }
    }, [game.id]);

    const updateTeam2 = useCallback((data) => {
        if (data.id === game.id) {
            setTeam2(data.team2);
        }
    }, [game.id]);

    const updateLoser = useCallback((data) => {
        if (data.id === game.id) {
            setLoser(data.loser);
        }
    }, [game.id]);

    useEffect(() => {
        if (!game) {
            return;
        }
        socket.on('scoreChange', updateScores);
        socket.on('gameStatusChange', updateStatus);
        socket.on('gameTeam1Change', updateTeam1);
        socket.on('gameTeam2Change', updateTeam2);
        socket.on('gameLoserChange', updateLoser);
        return () => {
            socket.off('scoreChange', updateScores);
            socket.off('gameStatusChange', updateStatus);
            socket.off('gameTeam1Change', updateTeam1);
            socket.off('gameTeam2Change', updateTeam2);
            socket.off('gameLoserChange', updateLoser);
        }
    }, [game, updateScores, updateStatus, updateTeam1, updateTeam2, updateLoser]);

    if (!game) {
        return null;
    }

    return (
        <Link href={`/game/${game.id}`} target="_blank">
            <Grid
                className={style.gameRR}
                columns={admin ? 'max-content 1fr max-content 1fr max-content' : '1fr max-content 1fr'}
            >
                {admin && game.status && game.status !== 'pending' && (
                    <Box
                        className={style.gameScore}
                        align="center"
                        justify="center"
                        bgColor={score1 > score2 ? 'secondary' : 'black'}
                        txtColor="white"
                    >
                        <Type font="control" weight={score1 > score2 ? 'bold' : null} mb="0">{score1}</Type>
                    </Box>
                )}

                <TeamCard team={team1} place={game.place1} loser={loser} />

                <div>
                    <div
                        className={style.gameRRTime}
                    >
                        {status === 'live' && (<Status status="LIVE" color="primary"/>)}
                        {status === 'forfeit' && (<Status status="FORFEIT"/>)}
                        {status === 'ended' && (<Status status="FINAL"/>)}
                        {(status === 'pending' || status === 'waiting') && (
                            <Type tag="p" weight="bold" align="center" font="small" className={style.gameTime} mb="0" uppercase>
                                {dayjs(game.time).tz('America/New_York').format('ddd')}
                                <br />
                                {dayjs(game.time).tz('America/New_York').format('h:mm')}
                            </Type>
                        )}
                    </div>
                </div>

                <TeamCard team={team2} place={game.place2} loser={loser} textAlign="right" />

                {admin && game.status && game.status !== 'pending' && (
                    <Box
                        className={style.gameScore}
                        bgColor={score2 > score1 ? 'secondary' : 'black'}
                        align="center"
                        justify="center"
                        txtColor="white"
                    >
                        <Type font="control" weight={score2 > score1 ? 'bold' : null}>{score2}</Type>
                    </Box>
                )}
            </Grid>
        </Link>
    );
}
export default Game;
